import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';

const request = createAppRequest();
const BASE_PATH = '/bff/zy-crm';

export type TaxonomyType = 'flat' | 'hierarchical';
export type CrmTaggableEntityType =
  | 'lead'
  | 'customer'
  | 'contact'
  | 'opportunity';

export type CrmTaxonomyTag = {
  uuid: string;
  taxonomyUuid: string;
  name: string;
  identifier: string;
  description?: string;
  parentUuid?: string;
  level: number;
  path?: string;
  color?: string;
  backgroundColor?: string;
  sortWeight: number;
  enabled: number | boolean;
  usageCount: number;
  children?: CrmTaxonomyTag[];
};

export type CrmTaxonomy = {
  uuid: string;
  name: string;
  identifier: string;
  description?: string;
  type: TaxonomyType;
  color?: string;
  sortWeight: number;
  enabled: number | boolean;
  tagCount: number;
  isSystem: number | boolean;
  sourceTemplateKey?: string;
  tags?: CrmTaxonomyTag[];
};

export type CrmTaxonomyTemplate = {
  key: string;
  name: string;
  description?: string;
  imported: boolean;
  tags: Array<Pick<CrmTaxonomyTag, 'name' | 'identifier' | 'color'>>;
};

export type CrmTagSummary = {
  namePath?: string;
  uuid: string;
  taxonomyUuid: string;
  taxonomyName: string;
  taxonomyIdentifier?: string;
  name: string;
  identifier: string;
  color?: string;
  backgroundColor?: string;
  enabled: boolean;
};

export type PageResult<T> = {
  list: T[];
  total: number;
  page?: number;
  pageSize?: number;
};

export function queryTaxonomies(
  params: {
    page?: number;
    pageSize?: number;
    keyword?: string;
    type?: TaxonomyType;
    enabled?: boolean;
  },
  options?: ApiRequestOptions,
) {
  return request<PageResult<CrmTaxonomy>>(`${BASE_PATH}/taxonomies`, {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

export function queryTaxonomyStatistics(options?: ApiRequestOptions) {
  return request<{ total: number; system: number; custom: number; enabled: number }>(
    `${BASE_PATH}/taxonomies/statistics`,
    { method: 'GET', ...(options || {}) },
  );
}

export function createTaxonomy(
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<CrmTaxonomy>(`${BASE_PATH}/taxonomies`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export function updateTaxonomy(
  taxonomyUuid: string,
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<CrmTaxonomy>(`${BASE_PATH}/taxonomies/${taxonomyUuid}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export function deleteTaxonomy(
  taxonomyUuid: string,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/taxonomies/${taxonomyUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export function queryTaxonomyTagTree(
  taxonomyUuid: string,
  options?: ApiRequestOptions,
) {
  return request<CrmTaxonomyTag[]>(
    `${BASE_PATH}/taxonomies/${taxonomyUuid}/tags/tree`,
    { method: 'GET', ...(options || {}) },
  );
}

export function createTaxonomyTag(
  taxonomyUuid: string,
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<CrmTaxonomyTag>(
    `${BASE_PATH}/taxonomies/${taxonomyUuid}/tags`,
    { method: 'POST', data, ...(options || {}) },
  );
}

export function updateTaxonomyTag(
  tagUuid: string,
  data: Record<string, unknown>,
  options?: ApiRequestOptions,
) {
  return request<CrmTaxonomyTag>(`${BASE_PATH}/taxonomy-tags/${tagUuid}`, {
    method: 'PATCH',
    data,
    ...(options || {}),
  });
}

export function deleteTaxonomyTag(
  tagUuid: string,
  options?: ApiRequestOptions,
) {
  return request<void>(`${BASE_PATH}/taxonomy-tags/${tagUuid}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

export function batchCreateTaxonomyPaths(
  taxonomyUuid: string,
  paths: string[],
  options?: ApiRequestOptions,
) {
  return request<{
    createdCount: number;
    reusedCount: number;
    failedCount: number;
    errors: Array<{ line: string; reason: string }>;
  }>(
    `${BASE_PATH}/taxonomies/${taxonomyUuid}/tags/batch-paths`,
    { method: 'POST', data: { lines: paths }, ...(options || {}) },
  );
}

export function queryTaxonomyTemplates(options?: ApiRequestOptions) {
  return request<CrmTaxonomyTemplate[]>(`${BASE_PATH}/taxonomies/templates`, {
    method: 'GET',
    ...(options || {}),
  });
}

export function importTaxonomyTemplate(
  templateKey: string,
  options?: ApiRequestOptions,
) {
  return request<{ alreadyImported: boolean; taxonomyUuid: string }>(
    `${BASE_PATH}/taxonomies/templates/${templateKey}/import`,
    { method: 'POST', ...(options || {}) },
  );
}

export function queryEntityTagOptions(
  keyword?: string,
  options?: ApiRequestOptions,
) {
  return request<Array<CrmTaxonomy & { tags: CrmTaxonomyTag[] }>>(
    `${BASE_PATH}/entity-tags/options`,
    {
      method: 'GET',
      params: keyword ? { keyword } : {},
      ...(options || {}),
    },
  );
}

export function queryEntityTags(
  entityType: CrmTaggableEntityType,
  entityUuid: string,
  options?: ApiRequestOptions,
) {
  return request<CrmTagSummary[]>(
    `${BASE_PATH}/entity-tags/${entityType}/${entityUuid}`,
    { method: 'GET', ...(options || {}) },
  );
}

export function replaceEntityTags(
  entityType: CrmTaggableEntityType,
  entityUuid: string,
  tagUuids: string[],
  options?: ApiRequestOptions,
) {
  return request<CrmTagSummary[]>(
    `${BASE_PATH}/entity-tags/${entityType}/${entityUuid}`,
    { method: 'PUT', data: { tagUuids }, ...(options || {}) },
  );
}

export function batchEntityTags(
  data: {
    entityType: CrmTaggableEntityType;
    entityUuids: string[];
    operation: 'add' | 'remove';
    tagUuids: string[];
  },
  options?: ApiRequestOptions,
) {
  return request<{ updated: number; entityUuids: string[] }>(
    `${BASE_PATH}/entity-tags/batch`,
    { method: 'POST', data, ...(options || {}) },
  );
}

export type QuickCreateTagInput = {
  entityType: CrmTaggableEntityType;
  name: string;
  taxonomyUuid?: string;
  newTaxonomyName?: string;
  parentUuid?: string;
  confirmUpgrade?: boolean;
};

export function quickCreateEntityTag(data: QuickCreateTagInput) {
  return request<{ taxonomyUuid: string; tagUuid: string; reused: boolean; namePath: string }>(
    `${BASE_PATH}/entity-tags/quick-create`, { method: 'POST', data },
  );
}

export function queryTagLibrary(options?: ApiRequestOptions) {
  return request<CrmTaxonomyTag[]>(`${BASE_PATH}/taxonomy-tags`, {
    method: 'GET', ...(options || {}),
  });
}

export function createLibraryTag(data: Record<string, unknown>, options?: ApiRequestOptions) {
  return request<CrmTaxonomyTag>(`${BASE_PATH}/taxonomy-tags`, {
    method: 'POST', data, ...(options || {}),
  });
}
